import React from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { JOB_STATUSES, convertTimeLetter, isWeb } from '@helpers';

import ColorWrapper from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale, width as screenWidth } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';

import Image from '@v2/context/Image/context';
import { SCREEN_PADDING_HORIZONTAL, TEST_IDS, TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import { LevelSubScreen } from '@v2/pages/common/enums';
import { getPreferredMachine, getOperationImageData, getScheduleAdherenceInfo, getOperationImageSize, getOperationItemStyle } from '@components/search/operations/helpers';
import Svg from '@styles/icons/svg';
import StatusBadge from '@components/common/StatusBadge';
import Empty from '@components/common/Empty';

const Item = ({
  isEmpty,
  itemSize = TILE_CONFIG.BASE_WIDTH,
  item,
  index,
  C,
  isSmallMode,
  isLineMode,
  open = () => {},
  effectiveWidth,
  isPopupMode,
  attachMode,
  shouldUseDefaultHorizontalPaddings,
  scale = initialScale,
  font = initialFont,
  statContextStorage = moment.utc().format(),
  getStatusConfig = () => {},
}) => {
  const style = getOperationItemStyle(isEmpty, isSmallMode, index, itemSize, scale);
  const imgSize = getOperationImageSize(isLineMode, itemSize, scale);

  const imageData = {
    ...getOperationImageData({
      item,
      imgSize,
      onPress: () => open(item, LevelSubScreen.DOCUMENTS),
      contextId: item.productionOrderId,
      borderRadius: TILE_CONFIG.BORDER_RADIUS,
    }),
    // showImageControl: true,
    attachMode,
  };

  if (isLineMode) {
    if (isEmpty) return null;
    return (
      <View style={{ width: '100%', alignItems: 'center' }}>
        <View style={{ width: effectiveWidth || screenWidth }}>
          <TouchableElement testID={TEST_IDS.searchScreen.operations.itemTile} onPress={() => open(item)}>
            {({ boxStyle }) => (
              <View
                style={[
                  boxStyle,
                  {
                    height: scale(60),
                    paddingHorizontal: scale(
                      shouldUseDefaultHorizontalPaddings ? SCREEN_PADDING_HORIZONTAL : isPopupMode ? 20 : SCREEN_PADDING_HORIZONTAL
                    ),
                  },
                ]}
              >
                <View style={{ width: '100%', flexDirection: 'row', flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
                  <View style={{ width: scale(TILE_CONFIG.LINE_MODE_WIDTH), height: scale(TILE_CONFIG.LINE_MODE_HEIGHT) }}>
                    <Image {...imageData} />
                  </View>
                  <View style={{ flex: 1, paddingLeft: scale(10) }}>
                    <Text numberOfLines={1}>
                      <Text style={[font['Button 1'], { color: C('Label/Light/Primary') }]}>{item.productionOrderNumber} </Text>
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{item.description ? `- ${item.description}` : ''}</Text>
                    </Text>
                  </View>
                </View>
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
    );
  }

  const RenderTime = ({ time }) => {
    return (
      <Text>
        <Text
          style={[
            font['Time 2'],
            {
              color: C('Label/Light/Primary'),
            },
          ]}
        >
          {time.h}
        </Text>
        <Text
          style={[
            font['Footnote'],
            {
              color: C('Label/Light/Primary'),
            },
          ]}
        >
          :
        </Text>
        <Text
          style={[
            font['Time 2'],
            {
              color: C('Label/Light/Primary'),
            },
          ]}
        >
          {time.m}
        </Text>
        <Text
          style={[
            font['Footnote'],
            {
              color: C('Label/Light/Primary'),
            },
          ]}
        >
          :
        </Text>
        <Text
          style={[
            font['Time 4'],
            {
              color: C('Label/Light/Primary'),
            },
          ]}
        >
          {time.s}
        </Text>
      </Text>
    );
  };

  const preferredMachine = getPreferredMachine(item.machines);

  const amountOfDays = moment.utc(statContextStorage).diff(item.scheduledStartDate, 'day');

  const NOT_STARTED = item.status === JOB_STATUSES.NOT_STARTED;

  const { offsetText, showBehindError } = getScheduleAdherenceInfo({ amountOfDays, isNotStarted: NOT_STARTED });

  const jobStatusConfig = getStatusConfig(item.status);

  return isEmpty ? (
    <View style={[{ display: 'flex', flexDirection: 'column' }, style]} />
  ) : (
    <View style={[{ display: 'flex', flexDirection: 'column' }, style]}>
      <View testID={TEST_IDS.searchScreen.operations.itemImage} style={[imgSize, { backgroundColor: C('Background/Light/Tile No Picture'), borderRadius: scale(TILE_CONFIG.BORDER_RADIUS) }]}>
        <Image {...imageData} />
      </View>
      {!isSmallMode && (
        <View style={{ position: 'absolute', zIndex: 5, top: scale(6), right: scale(6) }}>
          <StatusBadge C={C} config={jobStatusConfig} scale={scale} font={font} />
        </View>
      )}
      {!isSmallMode && showBehindError && (
        <View style={{ position: 'absolute', zIndex: 5, top: scale(9), left: scale(6), flexDirection: 'row' }}>
          <Svg icon="error" color="Label/Light/Warning" width={scale(16)} height={scale(15)} />
          <Text style={[font['Button 2'], { color: C('Label/Light/Warning'), marginLeft: scale(4) }]}>{i18n.t('Late')}</Text>
        </View>
      )}
      {!isSmallMode && (
        <TouchableOpacity testID={TEST_IDS.searchScreen.operations.itemTile} activeOpacity={1} onPress={() => open(item)}>
          <View>
            <View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: scale(12) }}>
                <View style={{ flexDirection: 'row', gap: scale(6), alignItems: 'center' }}>
                  <RenderTime {...{ time: convertTimeLetter(item.totalTimeActualSeconds) }} />
                  <Text
                    numberOfLines={1}
                    style={[
                      font['Footnote'],
                      {
                        color: C('Label/Light/Tertiary'),
                      },
                    ]}
                  >
                    /
                  </Text>
                  <RenderTime {...{ time: convertTimeLetter(item.totalTimeScheduledSeconds) }} />
                </View>
                <View style={{ flexDirection: 'row' }}>
                  <Text>
                    <Text
                      style={[
                        font['Time 1'],
                        {
                          color: C('Label/Light/Primary'),
                          marginRight: scale(3),
                        },
                      ]}
                    >
                      {item.quantityProduced}
                    </Text>
                    <Text
                      style={[
                        font['Footnote'],
                        {
                          color: C('Label/Light/Tertiary'),
                          marginRight: scale(3),
                        },
                      ]}
                    >
                      /
                    </Text>
                    <Text
                      style={[
                        font['Time 3'],
                        {
                          color: C('Label/Light/Tertiary'),
                          marginRight: scale(3),
                        },
                      ]}
                    >
                      {item.quantityTarget}
                    </Text>
                    <Text
                      style={[
                        font['Caption 2'],
                        {
                          color: C('Label/Light/Tertiary'),
                        },
                      ]}
                    >
                      {i18n.t('Pcs')}
                    </Text>
                  </Text>
                </View>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: scale(3),
                }}
              >
                <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]} numberOfLines={1} ellipsizeMode={'middle'}>
                  {`${item.productionOrderNumber}-${item.operationNumber}`}
                  <Empty />
                </Text>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: scale(3),
                }}
              >
                <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]} numberOfLines={1} ellipsizeMode={'middle'}>
                  {item.operationName} {i18n.t('on')} {preferredMachine?.machineName}
                  <Empty />
                </Text>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: scale(3),
                }}
              >
                <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]} numberOfLines={1} ellipsizeMode={'middle'}>
                  {item.partDescription}
                  <Empty />
                </Text>
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: scale(3),
                }}
              >
                <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]} numberOfLines={1} ellipsizeMode={'middle'}>
                  {offsetText}
                  <Empty />
                </Text>
              </View>
            </View>
          </View>
        </TouchableOpacity>
      )}
    </View>
  );
};

export default ColorWrapper(Item);
